<script setup lang="ts">
import { Camera, CheckCircle2, ExternalLink, Globe2, Hand, KeyRound, LoaderCircle, ShieldCheck } from 'lucide-vue-next'
import { httpsCallable } from 'firebase/functions'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { firebaseClient } from '@/firebase'
import { useAgentStore, useBrowserStore } from '@/stores/domain'

const store = useBrowserStore()
const { profiles, sessions, artifacts, audit } = storeToRefs(store)
const { agents } = storeToRefs(useAgentStore())
const selectedId = ref('')
const busy = ref('')
const error = ref('')

const liveProfiles = computed(() => profiles.value.filter((profile) => profile.status !== 'archived'))
const selected = computed(() => liveProfiles.value.find((item) => item.id === selectedId.value) ?? liveProfiles.value[0])
/** Sessions where an agent (or a sign-in the user started) is waiting on the user. */
const waiting = computed(() => sessions.value.filter((item) => item.status === 'waiting_for_user' && item.liveViewUrl))
const selectedArtifacts = computed(() => artifacts.value.filter((item) => item.profileId === selected.value?.id && item.url).slice(0, 12))
const selectedAudit = computed(() => audit.value.filter((item) => item.profileId === selected.value?.id).slice(0, 20))
const selectedSessions = computed(() => sessions.value.filter((item) => item.profileId === selected.value?.id && item.status === 'closed').slice(0, 8))
const agentName = (agentId: string) => agents.value.find((agent) => agent.id === agentId)?.name ?? 'Agent'
const profileName = (profileId: string) => profiles.value.find((profile) => profile.id === profileId)?.name ?? 'Agent browser'
const stamp = (value?: number) => (value ? new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '')
const message = (value: unknown, fallback: string) => (value instanceof Error ? value.message.replace('Firebase: ', '') : fallback)

const openLive = (url?: string) => { if (url) window.open(url, '_blank', 'noopener,noreferrer') }

const startLogin = async () => {
  if (!selected.value) return
  busy.value = 'login'; error.value = ''
  try {
    const response = await httpsCallable<{ profileId: string }, { liveViewUrl: string }>(firebaseClient.functions, 'startBrowserLogin')({ profileId: selected.value.id })
    openLive(response.data.liveViewUrl)
  } catch (value) { error.value = message(value, 'The browser could not be opened.') }
  finally { busy.value = '' }
}

const finish = async (sessionId: string) => {
  busy.value = sessionId; error.value = ''
  try { await httpsCallable(firebaseClient.functions, 'completeBrowserLogin')({ sessionId }) }
  catch (value) { error.value = message(value, 'Could not hand the browser back.') }
  finally { busy.value = '' }
}
</script>

<template>
  <div class="section-page">
    <header class="section-page__header"><div><span class="eyebrow">Agent browsers</span><h1>Browser Control</h1>
      <p>Each agent has its own browser that keeps its logins between tasks. Agents search, sign up, and work on websites; when a site needs you (a login, MFA, or a CAPTCHA), they hand the browser to you here. Browser time uses about 1 credit a minute.</p></div></header>
    <p v-if="error" class="connection-error" role="alert">{{ error }}</p>

    <section v-if="waiting.length" class="browser-waiting">
      <article v-for="session in waiting" :key="session.id" class="browser-waiting__item">
        <span class="browser-waiting__icon"><Hand :size="18" /></span>
        <div>
          <strong>{{ session.kind === 'user_login' ? `Signing in for ${profileName(session.profileId)}` : `${agentName(session.agentId)} needs you in its browser` }}</strong>
          <p>{{ session.handoffReason || 'Finish in the browser, then tell the agent you are done.' }}</p>
        </div>
        <div class="connection-actions">
          <button class="button button--primary button--small" type="button" @click="openLive(session.liveViewUrl)"><ExternalLink :size="15" /> Open browser</button>
          <button class="button button--ghost button--small" type="button" :disabled="Boolean(busy)" @click="finish(session.id)">
            <LoaderCircle v-if="busy === session.id" :size="15" class="spin" /><CheckCircle2 v-else :size="15" /> I'm done</button>
        </div>
      </article>
    </section>

    <section class="detail-grid">
      <article class="detail-card">
        <div class="detail-card__head"><div><span class="eyebrow">Browsers</span><h2>One per agent</h2></div><ShieldCheck :size="18" /></div>
        <div v-if="liveProfiles.length" class="team-list">
          <button v-for="profile in liveProfiles" :key="profile.id" type="button" :class="{ 'is-selected': selected?.id === profile.id }" @click="selectedId = profile.id">
            <i class="specialist-avatar specialist-avatar--blue"><Globe2 :size="15" /></i>
            <span><strong>{{ profile.name }}</strong><small>{{ profile.allowedDomains.length ? profile.allowedDomains.join(', ') : 'Any public site' }}{{ profile.authenticatedAt ? ` · signed in ${stamp(profile.authenticatedAt)}` : '' }}</small></span>
          </button>
        </div>
        <p v-else>An agent gets its browser the first time it needs one.</p>
      </article>
      <article class="detail-card">
        <div class="detail-card__head"><div><span class="eyebrow">Sign in</span><h2>Log in for an agent</h2></div><KeyRound :size="18" /></div>
        <template v-if="selected">
          <p>Open {{ selected.name }} and sign in to any site yourself. Your password and MFA stay in the browser and are never shown to the agent; the login is saved for the agent's future tasks.</p>
          <button class="button button--primary" type="button" :disabled="Boolean(busy) || selected.status === 'user_control'" @click="startLogin">
            <LoaderCircle v-if="busy === 'login'" :size="16" class="spin" /><ExternalLink v-else :size="16" /> Open browser to sign in</button>
        </template>
        <p v-else>Select a browser.</p>
      </article>
    </section>

    <section v-if="selected" class="detail-grid">
      <article class="detail-card"><div class="detail-card__head"><div><span class="eyebrow">Captures</span><h2>Screenshots</h2></div><Camera :size="18" /></div>
        <div v-if="selectedArtifacts.length" class="browser-shots">
          <a v-for="item in selectedArtifacts" :key="item.id" :href="item.url" target="_blank" rel="noopener noreferrer" :title="item.pageUrl">
            <img :src="item.url" :alt="`Screenshot of ${item.pageUrl ?? 'a page'}`" loading="lazy" /><small>{{ stamp(item.createdAt) }}</small></a>
        </div><p v-else>No screenshots yet.</p>
      </article>
      <article class="detail-card"><div class="detail-card__head"><div><span class="eyebrow">Activity</span><h2>Recent actions</h2></div><ShieldCheck :size="18" /></div>
        <div v-if="selectedSessions.length" class="team-list">
          <div v-for="item in selectedSessions" :key="item.id"><span><strong>{{ item.kind === 'user_login' ? 'You signed in' : agentName(item.agentId) }}</strong>
            <small>{{ stamp(item.startedAt) }} · {{ item.minutes ?? 1 }} min · {{ item.credits ?? 0 }} credits</small></span></div>
        </div>
        <div v-if="selectedAudit.length" class="team-list"><div v-for="item in selectedAudit" :key="item.id"><span><strong>{{ item.action }}</strong><small>{{ item.domain || 'browser' }} · {{ item.result.replace('_', ' ') }} · {{ stamp(item.createdAt) }}</small></span></div></div>
        <p v-else-if="!selectedSessions.length">No browser activity yet.</p>
      </article>
    </section>
  </div>
</template>
